// Archive zip sans compression (méthode « stockée » de la norme APPNOTE) : les justificatifs
// d'un lot, des PDF et des photos déjà compressés, se téléchargent d'un coup. Noms en UTF-8
// (bit 11), chaque fichier daté du jour à minuit ; ni zip64 ni chiffrement.

export type FichierArchive = { nom: string; octets: Uint8Array }

const TABLE_CRC = Uint32Array.from({ length: 256 }, (_, n) => {
  let c = n
  for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
  return c >>> 0
})

export function crc32(octets: Uint8Array): number {
  let c = 0xffffffff
  for (const o of octets) c = TABLE_CRC[(c ^ o) & 0xff]! ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

const MAX_32 = 0xffffffff

/** L'archive des fichiers, dans leur ordre ; `jour` (AAAA-MM-JJ) les date tous. */
export function archiveZip(fichiers: readonly FichierArchive[], jour: string): Uint8Array {
  const vus = new Set<string>()
  for (const f of fichiers) {
    if (vus.has(f.nom)) throw new Error(`Deux fichiers nommés « ${f.nom} » dans l’archive.`)
    vus.add(f.nom)
  }
  if (fichiers.length > 0xffff) throw new Error('Trop de fichiers pour une archive.')
  const [a, m, j] = jour.split('-').map(Number) as [number, number, number]
  const dateDos = ((a - 1980) << 9) | (m << 5) | j
  const encodeur = new TextEncoder()
  const entrees = fichiers.map((f) => ({
    nom: encodeur.encode(f.nom),
    octets: f.octets,
    crc: crc32(f.octets),
  }))
  const tailleLocale = entrees.reduce((n, e) => n + 30 + e.nom.byteLength + e.octets.byteLength, 0)
  const tailleCentrale = entrees.reduce((n, e) => n + 46 + e.nom.byteLength, 0)
  if (tailleLocale > MAX_32) throw new Error('Archive trop lourde (4 Go au plus).')
  const zip = new Uint8Array(tailleLocale + tailleCentrale + 22)
  const v = new DataView(zip.buffer)

  // En-têtes communs à l'entrée locale (à partir de 4) et à celle du répertoire (à partir de 6).
  const champs = (p: number, e: (typeof entrees)[number]) => {
    v.setUint16(p, 20, true) // version requise : 2.0
    v.setUint16(p + 2, 0x0800, true) // noms en UTF-8
    v.setUint16(p + 4, 0, true) // stocké
    v.setUint16(p + 6, 0, true) // minuit
    v.setUint16(p + 8, dateDos, true)
    v.setUint32(p + 10, e.crc, true)
    v.setUint32(p + 14, e.octets.byteLength, true)
    v.setUint32(p + 18, e.octets.byteLength, true)
    v.setUint16(p + 22, e.nom.byteLength, true)
  }

  let p = 0
  const positions: number[] = []
  for (const e of entrees) {
    positions.push(p)
    v.setUint32(p, 0x04034b50, true)
    champs(p + 4, e)
    zip.set(e.nom, p + 30)
    zip.set(e.octets, p + 30 + e.nom.byteLength)
    p += 30 + e.nom.byteLength + e.octets.byteLength
  }
  for (const [i, e] of entrees.entries()) {
    v.setUint32(p, 0x02014b50, true)
    v.setUint16(p + 4, 20, true) // créé par : version 2.0
    champs(p + 6, e)
    v.setUint32(p + 42, positions[i]!, true)
    zip.set(e.nom, p + 46)
    p += 46 + e.nom.byteLength
  }
  v.setUint32(p, 0x06054b50, true)
  v.setUint16(p + 8, entrees.length, true)
  v.setUint16(p + 10, entrees.length, true)
  v.setUint32(p + 12, tailleCentrale, true)
  v.setUint32(p + 16, tailleLocale, true)
  return zip
}
